import { Alert, Button, Form, Input, notification } from 'antd';
import React, { useState } from 'react';
import { useUpdateBillingInfoMutation } from '../service';
import validate from 'validate.js';
import { firstLastNameValidationRule } from '../utils';

function getInitialInputState(initialValue: string) {
    return {
        value: initialValue,
        error: '',
    };
}

type Props = {
    firstName: string;
    lastName: string;
    company: string;
    vatNumber: string;
};

export default function BillingInfoForm({ firstName, lastName, company, vatNumber }: Props) {
    const [firstNameInput, setFirstNameInput] = useState(() => getInitialInputState(firstName));
    const [lastNameInput, setLastNameInput] = useState(() => getInitialInputState(lastName));
    const [companyValue, setCompanyValue] = useState(company);
    const [vatNumberValue, setVatNumberValue] = useState(vatNumber);

    const [updateBillingInfo, { isLoading: isUpdatingBillingInfo }] = useUpdateBillingInfoMutation();

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const validationErrors = validate(
            {
                firstName: firstNameInput.value,
                lastName: lastNameInput.value,
            },
            firstLastNameValidationRule
        );

        if (validationErrors) {
            if (validationErrors.firstName) {
                setFirstNameInput((input) => ({ value: input.value, error: validationErrors.firstName[0] }));
            }
            if (validationErrors.lastName) {
                setLastNameInput((input) => ({ value: input.value, error: validationErrors.lastName[0] }));
            }
            return;
        }

        try {
            await updateBillingInfo({
                first_name: firstNameInput.value,
                last_name: lastNameInput.value,
                company: companyValue,
                vat_number: vatNumberValue,
            }).unwrap();
        } catch (error) {
            notification.error({ message: 'Error', description: 'Cannot update billing information. Please try again or contact support!' });
        }
    };

    return (
        <div className="billing-info-container">
            <Alert
                className="message"
                message="Changes to your billing information will take effect starting with next scheduled payment and will be
              reflected on your next invoice"
                type="info"
                showIcon
            />
            <Form
                className="billing-info-form"
                onSubmit={handleFormSubmit}
            >
                <Form.Item
                    label="First name"
                    validateStatus={firstNameInput.error ? 'error' : 'validating'}
                    help={firstNameInput.error ? firstNameInput.error : ''}
                >
                    <Input
                        name="firstName"
                        type="text"
                        value={firstNameInput.value}
                        onChange={(event) => setFirstNameInput({ value: event.target.value, error: '' })}
                    />
                </Form.Item>
                <Form.Item
                    label="Last name"
                    validateStatus={lastNameInput.error ? 'error' : 'validating'}
                    help={lastNameInput.error ? lastNameInput.error : ''}
                >
                    <Input
                        name="lastName"
                        type="text"
                        value={lastNameInput.value}
                        onChange={(event) => setLastNameInput({ value: event.target.value, error: '' })}
                    />
                </Form.Item>
                <Form.Item label="Company name">
                    <Input
                        name="company"
                        type="text"
                        value={companyValue}
                        onChange={(event) => setCompanyValue(event.target.value)}
                    />
                </Form.Item>
                <Form.Item label="VAT number">
                    <Input
                        name="vatNumber"
                        type="text"
                        value={vatNumberValue}
                        onChange={(event) => setVatNumberValue(event.target.value)}
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isUpdatingBillingInfo}
                    >
                        {isUpdatingBillingInfo ? 'Saving...' : 'Save'}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
}
